import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { DepartmentMembership } from '@/plugins/auth'
import { setDepartmentIdGetter } from '@/plugins/api'
import { useAuthStore } from './auth'
import { useTaskFilterStore } from './taskFilter'

/**
 * DepartmentStore — управление multitenancy контекстом.
 *
 * Позволяет superadmin переключаться между департаментами,
 * обычные пользователи работают в своём primary департаменте.
 */
export const useDepartmentStore = defineStore('department', () => {
  const authStore = useAuthStore()

  // Current selected department ID (persisted in localStorage for superadmin)
  const _currentDepartmentId = ref<string | undefined>(undefined)

  // Available departments for current user
  const availableDepartments = computed<DepartmentMembership[]>(() => {
    return authStore.user?.departmentMemberships || []
  })

  // Current department ID with fallback to user's default
  const currentDepartmentId = computed<string | undefined>(() => {
    // If explicitly set (by superadmin switching), use that
    if (_currentDepartmentId.value) {
      // Validate it's still in available list or user is superadmin
      const isValid =
        authStore.isSuperAdmin ||
        availableDepartments.value.some(
          (d) => d.departmentId === _currentDepartmentId.value
        )
      if (isValid) {
        return _currentDepartmentId.value
      }
    }

    // Otherwise use user's currentDepartmentId from auth
    return authStore.user?.currentDepartmentId
  })

  // Current department info
  const currentDepartment = computed<DepartmentMembership | undefined>(() => {
    if (!currentDepartmentId.value) return undefined
    return availableDepartments.value.find(
      (d) => d.departmentId === currentDepartmentId.value
    )
  })

  // Check if user can switch departments (superadmin only)
  const canSwitchDepartment = computed(() => authStore.isSuperAdmin)

  /**
   * Initialize from localStorage (for superadmin persistence)
   * and register API interceptor getter.
   */
  function init() {
    // Register department ID getter for API interceptor
    setDepartmentIdGetter(() => currentDepartmentId.value)

    // Restore superadmin selection from localStorage
    if (authStore.isSuperAdmin) {
      const saved = localStorage.getItem('vavt_department_id')
      if (saved) {
        _currentDepartmentId.value = saved
      }
    }
  }

  /**
   * Switch to a different department (superadmin only).
   * Invalidates relevant caches.
   */
  async function switchDepartment(departmentId: string | undefined) {
    if (!authStore.isSuperAdmin) {
      console.warn('[DepartmentStore] Only superadmin can switch departments')
      return
    }

    const previousId = _currentDepartmentId.value
    _currentDepartmentId.value = departmentId

    // Persist in localStorage
    if (departmentId) {
      localStorage.setItem('vavt_department_id', departmentId)
    } else {
      localStorage.removeItem('vavt_department_id')
    }

    // Invalidate caches if department changed
    if (previousId !== departmentId) {
      await invalidateCaches()
    }
  }

  /**
   * Clear department selection (use user's default)
   */
  function clearSelection() {
    _currentDepartmentId.value = undefined
    localStorage.removeItem('vavt_department_id')
  }

  /**
   * Invalidate all relevant caches after department switch.
   * This ensures lists reload with new department filter.
   */
  async function invalidateCaches() {
    console.log('[DepartmentStore] Invalidating caches after department switch')

    // Reset task filter store to trigger reload
    try {
      const taskFilterStore = useTaskFilterStore()
      taskFilterStore.$reset()
    } catch {
      // Store might not be initialized
    }

    // Emit custom event for components to react
    window.dispatchEvent(
      new CustomEvent('department-changed', {
        detail: { departmentId: currentDepartmentId.value },
      })
    )
  }

  return {
    // State
    currentDepartmentId,
    availableDepartments,
    currentDepartment,
    canSwitchDepartment,

    // Actions
    init,
    switchDepartment,
    clearSelection,
    invalidateCaches,
  }
})
